import { TypedHTTP } from '@flakiness/shared/common/typedHttp.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { css, html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../api.js';
import { consume, contexts, ContextTask, FKOrgChanged } from '../contexts.js';
import { linkStyles } from './cssstyles.js';

@customElement('organization-context')
export class OrganizationContext extends LitElement {

  static render(options: {
    orgSlug?: string,
  }, e: TemplateResult) {
    // Re-use the same TemplateResult so that the resulting element
    // could be efficiently rerendered by clients.
    return html`
      <organization-context
        .orgSlug=${options.orgSlug}
      >${e}</organization-context>
    `;
  }

  @property({ attribute: false }) orgSlug?: string;

  @consume({ context: contexts.notLoggedIn, subscribe: true }) @state() private _notLoggedIn?: ContextType<typeof contexts.notLoggedIn>;  

  private _orgTask = new ContextTask(this, contexts.org, {
    args: () => [this.orgSlug] as const,
    task: async ([orgSlug], { signal }) => {
      CHECK(orgSlug);
      const org = await api.user.findOrganization.GET({ orgSlug }, { signal });
      return org;
    }
  });

  private _orgBilling = new ContextTask(this, contexts.billing, {
    args: () => [this._orgTask.value] as const,
    task: async ([org], { signal }) => {
      if (!org)
        return undefined;
      // The method will fail if the user has no access to the billing information.
      return await api.billing.status.GET({ orgSlug: org.orgSlug }, { signal }).catch(e => undefined);
    }
  });

  private _orgMembers = new ContextTask(this, contexts.orgMembers, {
    args: () => [this._orgTask.value],
    task: async ([org], { signal }) => {
      CHECK(org);
      return await api.organization.listMembers.GET({ orgSlug: org.orgSlug }, { signal });
    }
  });

  override render(): unknown {
    const err = this._orgTask.render({
      error: (error: any) => error,
    });
    const isNotFound = err?.status === TypedHTTP.StatusCodes.ClientErrors.NOT_FOUND;
    const isPaymentRequired = err?.status === TypedHTTP.StatusCodes.ClientErrors.PAYMENT_REQUIRED;
    const loginRequired = this._notLoggedIn && isNotFound;
    return html`
      <section @fk-org-changed=${(event: FKOrgChanged) => {
        if (event.orgSlug.toLowerCase() !== this.orgSlug?.toLowerCase())
          return;
        this._orgTask.run();
        this._orgMembers.run();
        this._orgBilling.run();
      }}>${loginRequired ? html`<page-login></page-login>` :
          isPaymentRequired ? html`<page-http-error code=402></page-http-error>` : 
          isNotFound ? html`<page-http-error code=404></page-http-error>` : html`<slot></slot>`}
      </section>
    `;
  }

  static styles = [linkStyles, css`
    :host, section {
      display: contents;
    }
  `];
}
